import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ChevronLeft, ChevronRight, Copy, Check } from 'lucide-react';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { adminListAuditLogs } from '../../utils/database';
import type { AuditLogEntry } from '../../utils/database';
const ACTION_TYPE_OPTIONS = ['', 'login', 'get-user', 'user-list', 'user-upsert', 'user-delete', 'admin-user-list', 'admin-user-upsert', 'admin-user-delete', 'admin-audit-log-list', 'sync-trigger', 'sync-run', 'sync-run-worker', 'ai-agent-prompt', 'ai-agent-ondemand-non-mcp', 'ai-agent-ondemand-mcp', 'submit-feedback', 'admin-upload-untracked-data', 'admin-export-untracked-data', 'admin-counts-refresh', 'get-report-data', 'get-reports-config', 'admin-config-seed', 'org-lookup', 'wf-get-report'];
const PAGE_SIZE = 50;
function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}
function truncate(str: unknown, max = 80): string {
  if (str === null || str === undefined) return '—';
  const s = typeof str === 'string' ? str : JSON.stringify(str);
  return s.length > max ? s.substring(0, max) + '...' : s;
}
function extractDetails(log: AuditLogEntry): Record<string, unknown> {
  return Object.entries(log).filter(([k]) => !['_id', 'timestamp', 'actionType', 'email', 'accessLevel'].includes(k)).reduce((acc, [k, v]) => ({
    ...acc,
    [k]: v
  }), {});
}
function CopyButton({
  text
}: {
  text: string;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: select text for manual copy
    }
  };
  return <button onClick={e => {
    e.stopPropagation();
    void handleCopy();
  }} className="ml-1.5 p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors inline-flex flex-shrink-0" title="Copy details" data-id="element-281" data-mp="1d5od114p8qo8">
      {copied ? <Check size={12} className="text-emerald-500" data-id="element-282" data-mp="xahxsirf6p9w" /> : <Copy size={12} data-id="element-283" data-mp="xb9kt56ybeok" />}
    </button>;
}
const auditColumns: SortableColumn<AuditLogEntry>[] = [{
  key: 'timestamp',
  label: 'Timestamp',
  width: '20%',
  render: row => <span className="text-xs text-slate-500 whitespace-nowrap" data-id="element-284" data-mp="168a6h42myyob">{formatTimestamp(row.timestamp)}</span>,
  sortValue: row => row.timestamp
}, {
  key: 'actionType',
  label: 'Action',
  width: '18%',
  render: row => <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700" data-id="element-285" data-mp="1b9hcoyp4rpsv">
        {row.actionType}
      </span>,
  sortValue: row => row.actionType
}, {
  key: 'email',
  label: 'Actor',
  width: '18%',
  render: row => <span className="text-xs text-slate-700 font-medium" data-id="element-286" data-mp="3d2npf6dg6nyi">{row.email || '—'}</span>,
  sortValue: row => row.email || ''
}, {
  key: 'details',
  label: 'Details',
  width: '44%',
  render: row => <div className="flex items-center text-xs text-slate-500 max-w-md" data-id="element-287" data-mp="1isqlapptbkgv">
        <span className="truncate flex-1 min-w-0" data-id="element-288" data-mp="1isgvb24rpdb6">
          {truncate(extractDetails(row), 120)}
        </span>
        <CopyButton text={JSON.stringify(extractDetails(row), null, 2)} data-id="element-289" data-mp="1nph079pvsa0p" />
      </div>
}];
export function AuditLogTab() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterActor, setFilterActor] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        offset,
        limit: PAGE_SIZE
      };
      if (filterAction) params.actionType = filterAction;
      if (filterActor) params.actorEmail = filterActor;
      if (filterStartDate) params.startDate = filterStartDate;
      if (filterEndDate) params.endDate = filterEndDate;
      const data = await adminListAuditLogs(params);
      setLogs(data.logs);
      setTotalCount(data.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [offset, filterAction, filterActor, filterStartDate, filterEndDate]);
  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const applyFilters = () => {
    setOffset(0);
    // loadLogs will be triggered by useEffect when deps change
  };
  return <div className="space-y-4" data-id="element-290" data-mp="242umfuu3hxed">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-291" data-mp="292qsdzmcrywk">
        <div data-id="element-292" data-mp="2915xg8w4mdy6">
          <h3 className="text-base font-semibold text-slate-900" data-id="element-293" data-mp="294tnuz69yk8o">Audit Log</h3>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-294" data-mp="2e2bnu02bdp4j">
            System activity trail ({totalCount} total entries)
          </p>
        </div>
        <button onClick={() => void loadLogs()} disabled={loading} className="p-2 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50" title="Refresh" data-id="element-295" data-mp="1ldeiexj7bmt7">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} data-id="element-296" data-mp="1uc9s4jlgff19" />
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-id="element-297" data-mp="1z6dyp1qt5l1t">
        <div className="grid grid-cols-4 gap-3" data-id="element-298" data-mp="12menzgsxtksj">
          <div data-id="element-299" data-mp="16nqgpv73ym4y">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-300" data-mp="16ngt2e9jcr2f">Action Type</label>
            <select value={filterAction} onChange={e => setFilterAction(e.target.value)} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-301" data-mp="29ye184k1ycty">
              <option value="" data-id="element-302" data-mp="2epmcz78uuaaa">All actions</option>
              {ACTION_TYPE_OPTIONS.filter(Boolean).map(action => <option key={action} value={action} data-id="element-303" data-mp="1r134aqlw7vho">{action}</option>)}
            </select>
          </div>
          <div data-id="element-304" data-mp="1m31k22cqdq4m">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-305" data-mp="1m3jrlv4ufqru">Actor Email</label>
            <input type="text" value={filterActor} onChange={e => setFilterActor(e.target.value)} placeholder="Search by email..." className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-306" data-mp="1zyn743zelzue" />
          </div>
          <div data-id="element-307" data-mp="3ewrgpyjgjosy">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-308" data-mp="3f0fb0zwq8f8q">Start Date</label>
            <input type="datetime-local" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-309" data-mp="3jv2s4dpei7da" />
          </div>
          <div data-id="element-310" data-mp="6h7p13yfqeg0">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-311" data-mp="6hpv0sawg714">End Date</label>
            <input type="datetime-local" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-312" data-mp="3nxzemfi2anmi" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2" data-id="element-313" data-mp="2lbn7r1v6n9ig">
          <button onClick={applyFilters} className="px-4 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors" data-id="element-314" data-mp="2ld6nmrcxfijp">
            Apply Filters
          </button>
          <button onClick={() => {
          setFilterAction('');
          setFilterActor('');
          setFilterStartDate('');
          setFilterEndDate('');
          setOffset(0);
        }} className="px-4 py-1.5 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors" data-id="element-315" data-mp="27kqr89ej6irr">
            Clear
          </button>
        </div>
      </div>

      {/* Error */}
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" data-id="element-316" data-mp="1juwfjv5y7093">
          {error}
        </div>}

      {/* Table */}
      {loading && logs.length === 0 ? <div className="flex items-center justify-center py-12 text-slate-400 text-sm" data-id="element-317" data-mp="14rw9uqar7n7n">
          Loading audit logs...
        </div> : logs.length === 0 ? <div className="flex items-center justify-center py-12 text-slate-400 text-sm" data-id="element-318" data-mp="19r8xhd9g8yba">
          No audit log entries found.
        </div> : <SortableTable columns={auditColumns} data={logs} rowKey={row => row._id} defaultSort={{
      key: 'timestamp',
      dir: 'desc'
    }} data-id="element-319" data-mp="1jhajlzi8s6nk" />}

      {/* Pagination */}
      {totalPages > 1 && <div className="flex items-center justify-between text-sm text-slate-500" data-id="element-320" data-mp="1sfmb03qxn8h2">
          <span data-id="element-321" data-mp="1xhv5j4jgeila">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, totalCount)} of {totalCount}
          </span>
          <div className="flex items-center gap-1" data-id="element-322" data-mp="22dk87gqrsflt">
            <button onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))} disabled={offset === 0} className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-323" data-mp="33amsxep0x4j">
              <ChevronLeft size={16} data-id="element-324" data-mp="7x97byf4t1mn" />
            </button>
            <span className="px-2 text-xs" data-id="element-325" data-mp="7xsrzmw6244d">
              Page {Math.floor(offset / PAGE_SIZE) + 1} of {totalPages}
            </span>
            <button onClick={() => setOffset(o => Math.min((totalPages - 1) * PAGE_SIZE, o + PAGE_SIZE))} disabled={offset >= (totalPages - 1) * PAGE_SIZE} className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-326" data-mp="gy7akehqx546">
              <ChevronRight size={16} data-id="element-327" data-mp="vmv64krldeu4" />
            </button>
          </div>
        </div>}
    </div>;
}